import React from 'react';

/**
 * Classifies an emitted <script>/<Script> into one of two lifecycles:
 *
 * - `once` — should execute a single time on initial page load and persist
 *   across Next.js App Router client-side navigations. Appropriate for
 *   external assets, global bootstraps, analytics init, and code whose top
 *   level is only function/variable declarations.
 * - `on-route-change` — should re-execute on every navigation. Appropriate
 *   for scripts wrapped in `DOMContentLoaded`/`$(...).ready` (whose handler
 *   never re-fires after initial load), widget/DOM-init calls, and code that
 *   queries per-page DOM.
 *
 * Rules are evaluated in order; the first match wins. When in doubt the
 * default is `on-route-change` — mislabeling a truly-once script as
 * on-route-change only wastes work, while the reverse breaks pages after
 * navigation. Named-id scripts in `matchInlineScriptContent` bypass this
 * heuristic in favor of an explicit intent map.
 */
export type ScriptLifecycle = 'once' | 'on-route-change';

// Analytics/tag-manager bootstraps + known third-party once-loaders. Matches
// content that sets up a global queue or dispatches a library-load side effect
// exactly once per session; re-running would duplicate the init or pageview.
const ANALYTICS_PATTERN =
  /\bgtag\s*\(|\bdataLayer\s*[.=[]|GTM-[A-Z0-9]+|\bfbq\s*\(|\b_paq\.push|cctm\.xyz\/t\.js|tctm\.co\/t\.js/;
const READY_WRAPPER_PATTERN =
  /DOMContentLoaded|\$\s*\(\s*document\s*\)\s*\.\s*ready|\$\s*\(\s*function\s*\(/;
const WIDGET_INIT_PATTERN =
  /\bnew\s+Swiper\b|\.slick\s*\(|\.owlCarousel\s*\(|\binitGallery\s*\(|\binitSlider\s*\(/;
const DOM_QUERY_CALL_PATTERN =
  /document\.(?:getElementById|querySelector|querySelectorAll|forms)\s*[([]/;
const FORM_ID_PATTERN = /\bform_\d{3,4}_fld_\d+\b|#form_\d+/;
// Matches a body containing only whitespace, comments, function declarations,
// var/let/const statements, and `window.X = …` global exports — no top-level
// calls, no listener registrations.
const DECLARATIONS_ONLY_PATTERN =
  /^(?:\s|\/\/[^\n]*|\/\*[\s\S]*?\*\/|function\s+\w+\s*\([^)]*\)\s*\{[\s\S]*?\}|(?:var|let|const)\s+[^;]*;|window\.\w+\s*=\s*[^;]*;)*$/;

export const classifyScriptLifecycle = (
  src: string | undefined | null,
  content: string
): ScriptLifecycle => {
  if (src && !content.trim()) return 'once';
  if (ANALYTICS_PATTERN.test(content)) return 'once';
  if (READY_WRAPPER_PATTERN.test(content)) return 'on-route-change';
  if (WIDGET_INIT_PATTERN.test(content)) return 'on-route-change';
  if (DOM_QUERY_CALL_PATTERN.test(content)) return 'on-route-change';
  if (FORM_ID_PATTERN.test(content)) return 'on-route-change';
  if (DECLARATIONS_ONLY_PATTERN.test(content)) return 'once';
  return 'on-route-change';
};

// Explicit lifecycle for named-id inline scripts. Safer than re-deriving from
// CMS-mutable content — especially `broadstreet-loader`, where the generic
// heuristic would misclassify because of the top-level `broadstreet.loadNetworkJS(...)` call.
export const NAMED_ID_LIFECYCLE: Record<string, ScriptLifecycle> = {
  'broadstreet-script': 'once',
  'broadstreet-loader': 'once',
  'form-name-variables': 'on-route-change',
  'template-cp-header': 'on-route-change',
  'js-dom-eventlistener': 'on-route-change',
  'js-variables': 'once',
  'nfd-validate-form': 'once',
  checkDirectCamp: 'once',
  swiper: 'on-route-change',
  'acton-validation': 'on-route-change',
};

/**
 * Returns a `withLifecycle` helper bound to the caller's opt-in flag. When
 * `annotate` is false the helper is a no-op — output stays byte-identical for
 * callers that don't want the annotation. Respects a CMS-supplied
 * `data-script-lifecycle` attribute (either "once" or "on-route-change") over
 * our derived one, giving CMS authors an escape hatch when the heuristic
 * mis-classifies.
 */
export const makeWithLifecycle =
  (annotate: boolean | undefined) =>
  (
    element: React.ReactElement,
    lifecycle: ScriptLifecycle
  ): React.ReactElement => {
    if (!annotate) return element;
    const existing = (element.props as Record<string, unknown>)[
      'data-script-lifecycle'
    ];
    if (existing === 'once' || existing === 'on-route-change') return element;
    return React.cloneElement(
      element as React.ReactElement<Record<string, unknown>>,
      {
        'data-script-lifecycle': lifecycle,
      }
    );
  };
